import { API } from '../api.js';
// LevelsOverviewCard.jsx
// Phase 1 + 2 + 3:
//  • All-levels overview, gap analyzer, member distribution
//  • Preset templates, difficulty slider, inline editing, Save All
//  • Undo / Redo (Ctrl+Z / Ctrl+Y) with 20-step history
//  • Estimated time-to-level per card (configurable daily activity)
//  • XP Balance donut chart with activity assumptions editor

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import LevelSettingsModal from './LevelSettingsModal';
import styles from './LevelsOverviewCard.module.css';

/* ── Gap thresholds ─────────────────────────────────────────────────── */
const GAP_TOO_SMALL = 300;
const GAP_TOO_LARGE = 5000;

function gapWarning(gap, level) {
  if (level === 1) return null;
  if (gap < GAP_TOO_SMALL) return { type: 'easy', text: 'Too easy to skip' };
  if (gap > GAP_TOO_LARGE) return { type: 'hard', text: 'Members may drop off' };
  return null;
}

/* ── Slider ↔ XP (quadratic, max 20 000) ────────────────────────────── */
function sliderToXp(v)  { return Math.round(v * v * 2); }
function xpToSlider(xp) { return Math.min(100, Math.round(Math.sqrt(Math.max(0, xp) / 2))); }

/* ── Time formatter ─────────────────────────────────────────────────── */
function formatDays(days) {
  if (!isFinite(days) || days <= 0) return '< 1 day';
  if (days < 1)    return '< 1 day';
  if (days < 7)    return `~${Math.round(days)} day${Math.round(days) !== 1 ? 's' : ''}`;
  if (days < 30)   return `~${Math.round(days / 7)} wk`;
  if (days < 365)  return `~${Math.round(days / 30)} mo`;
  return `~${+(days / 365).toFixed(1)} yr`;
}

/* ── Preset templates ───────────────────────────────────────────────── */
const TEMPLATES = {
  gentle:   { label: '🐣 Gentle',       desc: 'Small frequent steps — great for active communities',   xps: [0, 150, 400, 800, 1300, 1900, 2600, 3500, 4500] },
  standard: { label: '⚡ Standard',     desc: 'Balanced default — works for most communities',          xps: [0, 500, 1200, 2200, 3500, 5200, 7200, 9500, 12500] },
  hardcore: { label: '💪 Hardcore',     desc: 'Large gaps — only dedicated members reach the top',      xps: [0, 1000, 2500, 5000, 9000, 14000, 20000, 27000, 37000] },
  course:   { label: '🎓 Course-Heavy', desc: 'Tuned for enrollment-driven communities',                xps: [0, 100, 300, 600, 1000, 1500, 2100, 2800, 3600] },
};

/* ── Default daily activity rates ──────────────────────────────────── */
const DEFAULT_RATES = { posts: 2, comments: 5, enrollments: 0.3, likesPosts: 3, likesComments: 5 };

/* ═══════════════════════════════════════════════════════════════════ */

export default function LevelsOverviewCard({ community, levels, distribution, totalMembers, onSaved }) {
  const { token } = useAuth();

  /* ── Core state ─────────────────────────────────────────────────── */
  const [draftLevels,     setDraftLevels]     = useState(() => levels.map(l => ({ ...l })));
  const [collapsed,       setCollapsed]       = useState(false);
  const [editingLevel,    setEditingLevel]    = useState(null);
  const [saving,          setSaving]          = useState(false);
  const [saveError,       setSaveError]       = useState(null);
  const [saveOk,          setSaveOk]          = useState(false);
  const [activeTemplate,  setActiveTemplate]  = useState(null);
  const [confirmTemplate, setConfirmTemplate] = useState(null);

  /* ── Phase 3: undo/redo ─────────────────────────────────────────── */
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  /* ── Phase 3: activity rates for time estimates ─────────────────── */
  const [rates,      setRates]      = useState(DEFAULT_RATES);
  const [showRates,  setShowRates]  = useState(false);

  /* ── Derived XP rates ───────────────────────────────────────────── */
  const postXp   = community?.postXp         ?? 30;
  const commXp   = community?.commentXp      ?? 15;
  const enrollXp = community?.enrollCourseXp ?? 100;
  const lPostXp  = community?.likePostXp     ?? 10;
  const lCommXp  = community?.likeCommentXp  ?? 5;

  const dailyXp = rates.posts       * postXp
                + rates.comments    * commXp
                + rates.enrollments * enrollXp
                + rates.likesPosts  * lPostXp
                + rates.likesComments * lCommXp;

  const dist      = distribution || {};
  const total     = totalMembers || 1;
  const maxCount  = Math.max(...draftLevels.map(l => dist[String(l.level)] || 0), 1);

  /* ── Dirty check ────────────────────────────────────────────────── */
  const isDirty = useMemo(() =>
    draftLevels.some((d, i) =>
      d.label !== levels[i]?.label || d.xpRequired !== levels[i]?.xpRequired
    ), [draftLevels, levels]);

  /* ── Undo/Redo helpers ──────────────────────────────────────────── */
  const pushUndo = useCallback((snapshot) => {
    setUndoStack(prev => [...prev.slice(-19), snapshot]);
    setRedoStack([]);
    setSaveOk(false);
  }, []);

  function undo() {
    if (undoStack.length === 0) return;
    const prev = undoStack[undoStack.length - 1];
    setRedoStack(r => [...r, draftLevels.map(l => ({ ...l }))]);
    setUndoStack(u => u.slice(0, -1));
    setDraftLevels(prev);
    setSaveOk(false);
  }

  function redo() {
    if (redoStack.length === 0) return;
    const next = redoStack[redoStack.length - 1];
    setUndoStack(u => [...u, draftLevels.map(l => ({ ...l }))]);
    setRedoStack(r => r.slice(0, -1));
    setDraftLevels(next);
    setSaveOk(false);
  }

  /* ── Keyboard shortcuts ─────────────────────────────────────────── */
  useEffect(() => {
    function onKey(e) {
      const ctrl = e.ctrlKey || e.metaKey;
      if (!ctrl) return;
      if (e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      if (e.key === 'y' || (e.key === 'z' && e.shiftKey)) { e.preventDefault(); redo(); }
      if (e.key === 's') { e.preventDefault(); if (isDirty && !saving) saveAll(); }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undoStack, redoStack, draftLevels, isDirty, saving]); // eslint-disable-line

  /* ── Field updater (pushes undo before change) ──────────────────── */
  const updateField = useCallback((levelNum, field, value) => {
    setDraftLevels(prev => {
      pushUndo(prev.map(l => ({ ...l })));
      return prev.map(l => l.level === levelNum ? { ...l, [field]: value } : l);
    });
    setSaveOk(false);
  }, [pushUndo]);

  /* ── Apply template ─────────────────────────────────────────────── */
  function applyTemplate(key) {
    pushUndo(draftLevels.map(l => ({ ...l })));
    const tpl = TEMPLATES[key];
    setDraftLevels(prev => prev.map((l, i) => ({ ...l, xpRequired: tpl.xps[i] ?? l.xpRequired })));
    setActiveTemplate(key);
    setConfirmTemplate(null);
    setSaveOk(false);
  }

  /* ── Reset ──────────────────────────────────────────────────────── */
  function reset() {
    pushUndo(draftLevels.map(l => ({ ...l })));
    setDraftLevels(levels.map(l => ({ ...l })));
    setActiveTemplate(null);
    setSaveOk(false);
    setSaveError(null);
  }

  /* ── Save All ───────────────────────────────────────────────────── */
  async function saveAll() {
    setSaving(true);
    setSaveError(null);
    setSaveOk(false);
    try {
      const merged = levels.map((orig, i) => ({
        ...orig,
        label:      draftLevels[i]?.label      ?? orig.label,
        xpRequired: draftLevels[i]?.xpRequired ?? orig.xpRequired,
      }));
      const res = await fetch(`${API}/api/communities/${community.id}`, {
        method:  'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body:    JSON.stringify({ levelConfigs: JSON.stringify(merged) }),
      });
      if (!res.ok) throw new Error('Failed to save');
      setSaveOk(true);
      setUndoStack([]);
      setRedoStack([]);
      onSaved?.();
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setSaving(false);
    }
  }

  /* ── Render ─────────────────────────────────────────────────────── */
  return (
    <div className={styles.card}>

      {/* ── Header ────────────────────────────────────────────────── */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.headerIcon}>⚙️</span>
          <div>
            <div className={styles.headerTitle}>Level Configuration</div>
            <div className={styles.headerSub}>
              {draftLevels.length} levels · {total} member{total !== 1 ? 's' : ''} total
              {isDirty && <span className={styles.dirtyPill}>● Unsaved</span>}
            </div>
          </div>
        </div>

        <div className={styles.headerRight}>
          {/* Undo / Redo */}
          <div className={styles.undoGroup}>
            <button
              className={styles.undoBtn}
              onClick={undo}
              disabled={undoStack.length === 0}
              title="Undo (Ctrl+Z)"
            >↩</button>
            <button
              className={styles.undoBtn}
              onClick={redo}
              disabled={redoStack.length === 0}
              title="Redo (Ctrl+Y)"
            >↪</button>
          </div>

          {isDirty && (
            <>
              <button className={styles.resetBtn} onClick={reset} disabled={saving}>Reset</button>
              <button className={styles.saveBtn}  onClick={saveAll} disabled={saving}>
                {saving ? 'Saving…' : 'Save All'}
              </button>
            </>
          )}
          {saveOk && !isDirty && <span className={styles.savedPill}>✓ Saved</span>}
          <button className={styles.collapseBtn} onClick={() => setCollapsed(v => !v)}>
            {collapsed ? 'Show ▼' : 'Hide ▲'}
          </button>
        </div>
      </div>

      {saveError && <div className={styles.saveError}>⚠️ {saveError}</div>}

      {!collapsed && (
        <>
          {/* ── Preset templates ──────────────────────────────────── */}
          <div className={styles.presetsSection}>
            <div className={styles.presetsLabel}>Quick Templates</div>
            <div className={styles.presets}>
              {Object.entries(TEMPLATES).map(([key, tpl]) => (
                <button
                  key={key}
                  className={`${styles.presetBtn} ${activeTemplate === key ? styles.presetBtnActive : ''}`}
                  onClick={() => setConfirmTemplate(key)}
                  title={tpl.desc}
                >{tpl.label}</button>
              ))}
            </div>
            {confirmTemplate && (
              <div className={styles.confirmBanner}>
                <span className={styles.confirmText}>
                  Apply <strong>{TEMPLATES[confirmTemplate].label}</strong>? {TEMPLATES[confirmTemplate].desc}.
                </span>
                <div className={styles.confirmBtns}>
                  <button className={styles.confirmNo}  onClick={() => setConfirmTemplate(null)}>Cancel</button>
                  <button className={styles.confirmYes} onClick={() => applyTemplate(confirmTemplate)}>Apply</button>
                </div>
              </div>
            )}
          </div>

          {/* ── Timeline rail ─────────────────────────────────────── */}
          <div className={styles.timeline}>
            {draftLevels.map((tier, i) => {
              const prev = draftLevels[i - 1];
              const gap  = prev ? (tier.xpRequired ?? 0) - (prev.xpRequired ?? 0) : 0;
              const warn = gapWarning(gap, tier.level);
              return (
                <div key={tier.level} className={styles.timelineStep}>
                  {i > 0 && (
                    <div className={styles.connectorWrap}>
                      <div className={`${styles.connector} ${warn ? styles['connector_' + warn.type] : ''}`} />
                      <div className={`${styles.gapLabel} ${warn ? styles['gap_' + warn.type] : ''}`}>
                        +{gap.toLocaleString()}{warn && <span className={styles.warnDot}>⚠️</span>}
                      </div>
                    </div>
                  )}
                  <button
                    className={styles.badge}
                    style={{ background: tier.color, boxShadow: `0 0 0 3px ${tier.color}33` }}
                    onClick={() => setEditingLevel(tier.level)}
                    title={`Advanced edit: L${tier.level} ${tier.label}`}
                  >{tier.icon || tier.level}</button>
                  <div className={styles.badgeLabel}>{tier.label}</div>
                  <div className={styles.badgeXp}>
                    {tier.level === 1 ? 'Start' : `${(tier.xpRequired ?? 0).toLocaleString()} XP`}
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Level cards grid ──────────────────────────────────── */}
          <div className={styles.grid}>
            {draftLevels.map((tier, i) => {
              const orig       = levels[i];
              const prev       = draftLevels[i - 1];
              const gap        = prev ? (tier.xpRequired ?? 0) - (prev.xpRequired ?? 0) : 0;
              const warn       = gapWarning(gap, tier.level);
              const count      = dist[String(tier.level)] || 0;
              const pct        = Math.round((count / total) * 100);
              const barWidth   = maxCount > 0 ? Math.round((count / maxCount) * 100) : 0;
              const xp         = tier.xpRequired ?? 0;
              const isModified = tier.label !== orig?.label || xp !== (orig?.xpRequired ?? 0);
              const estDays    = tier.level > 1 && dailyXp > 0 ? xp / dailyXp : null;

              return (
                <div
                  key={tier.level}
                  className={`${styles.levelCard} ${warn ? styles['levelCard_' + warn.type] : ''} ${isModified ? styles.levelCardModified : ''}`}
                >
                  {/* Header */}
                  <div className={styles.cardHeader}>
                    <div className={styles.cardBadge} style={{ background: tier.color }}>{tier.icon || tier.level}</div>
                    <div className={styles.cardMeta}>
                      <input
                        className={styles.nameInput}
                        value={tier.label}
                        onChange={e => updateField(tier.level, 'label', e.target.value)}
                        placeholder={`Level ${tier.level}`}
                        maxLength={24}
                      />
                      <div className={styles.cardXp}>
                        {tier.level === 1 ? '0 XP (Start)' : `${xp.toLocaleString()} XP`}
                        {isModified && <span className={styles.modifiedDot}>●</span>}
                      </div>
                    </div>
                    <button className={styles.editBtn} onClick={() => setEditingLevel(tier.level)} title="Advanced settings">✏️</button>
                  </div>

                  {/* ── Phase 3: Estimated time badge ─────────────── */}
                  {estDays !== null && (
                    <div className={styles.timeBadge} style={{ borderColor: tier.color + '55', color: tier.color }}>
                      <span className={styles.timeClock}>🕐</span>
                      <span>{formatDays(estDays)}</span>
                      <span className={styles.timeHint}>for active member</span>
                    </div>
                  )}

                  {/* Gap warning */}
                  {warn && (
                    <div className={`${styles.warnBanner} ${styles['warnBanner_' + warn.type]}`}>
                      ⚠️ {warn.text}{warn.type === 'easy' ? ` — only +${gap.toLocaleString()} XP` : ` — +${gap.toLocaleString()} XP gap`}
                    </div>
                  )}

                  {/* Slider + XP input */}
                  {tier.level > 1 && (
                    <div className={styles.sliderSection}>
                      <div className={styles.sliderLabel}>
                        <span>Difficulty</span>
                        <div className={styles.xpInputWrap}>
                          <input
                            type="number"
                            className={styles.xpInput}
                            value={xp}
                            min={0}
                            max={20000}
                            onChange={e => updateField(tier.level, 'xpRequired', Math.max(0, parseInt(e.target.value) || 0))}
                          />
                          <span className={styles.xpUnit}>XP</span>
                        </div>
                      </div>
                      <input
                        type="range"
                        className={styles.slider}
                        min={0} max={100}
                        value={xpToSlider(xp)}
                        style={{ '--thumb-color': tier.color }}
                        onChange={e => updateField(tier.level, 'xpRequired', sliderToXp(parseInt(e.target.value)))}
                      />
                      <div className={styles.sliderTicks}>
                        <span>Easy</span><span>Medium</span><span>Hard</span>
                      </div>
                    </div>
                  )}

                  {/* Distribution bar */}
                  <div className={styles.distRow}>
                    <div className={styles.distBar}>
                      <div className={styles.distFill} style={{ width: `${barWidth}%`, background: tier.color }} />
                    </div>
                    <div className={styles.distLabel}>{count} member{count !== 1 ? 's' : ''} ({pct}%)</div>
                  </div>

                  {/* Effort breakdown */}
                  {tier.level > 1 && xp > 0 && (
                    <div className={styles.effort}>
                      <div className={styles.effortTitle}>To reach this level:</div>
                      <div className={styles.effortRow}><span className={styles.effortNum}>{Math.ceil(xp / postXp).toLocaleString()}</span><span className={styles.effortAct}>posts (+{postXp} XP)</span></div>
                      <div className={styles.effortRow}><span className={styles.effortNum}>{Math.ceil(xp / commXp).toLocaleString()}</span><span className={styles.effortAct}>comments (+{commXp} XP)</span></div>
                      <div className={styles.effortRow}><span className={styles.effortNum}>{Math.ceil(xp / enrollXp).toLocaleString()}</span><span className={styles.effortAct}>enrollments (+{enrollXp} XP)</span></div>
                    </div>
                  )}

                  {/* Perks */}
                  {tier.perks?.length > 0 && (
                    <div className={styles.perksList}>
                      {tier.perks.slice(0, 2).map((p, j) => <div key={j} className={styles.perkItem}>🎁 {p}</div>)}
                      {tier.perks.length > 2 && <div className={styles.perkMore}>+{tier.perks.length - 2} more · click ✏️</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── Gap Analyzer ──────────────────────────────────────── */}
          <GapAnalyzerSummary levels={draftLevels} />

          {/* ── Phase 3: XP Balance Chart ─────────────────────────── */}
          <XPBalanceChart
            postXp={postXp} commXp={commXp} enrollXp={enrollXp}
            lPostXp={lPostXp} lCommXp={lCommXp}
            rates={rates} setRates={setRates}
            showRates={showRates} setShowRates={setShowRates}
            dailyXp={dailyXp}
          />

          {/* ── Save bar ──────────────────────────────────────────── */}
          {isDirty && (
            <div className={styles.saveBar}>
              <span className={styles.saveBarText}>You have unsaved changes. <kbd>Ctrl+S</kbd> to save · <kbd>Ctrl+Z</kbd> to undo</span>
              <div className={styles.saveBarBtns}>
                <button className={styles.resetBtn} onClick={reset} disabled={saving}>Reset</button>
                <button className={styles.saveBtn}  onClick={saveAll} disabled={saving}>
                  {saving ? 'Saving…' : '💾 Save All Changes'}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {editingLevel && (
        <LevelSettingsModal
          levelNumber={editingLevel}
          community={community}
          onClose={() => setEditingLevel(null)}
          onSaved={() => { setEditingLevel(null); onSaved?.(); }}
        />
      )}
    </div>
  );
}

/* ── Gap Analyzer Summary ───────────────────────────────────────────── */
function GapAnalyzerSummary({ levels }) {
  const warnings = [];
  for (let i = 1; i < levels.length; i++) {
    const tier = levels[i], prev = levels[i - 1];
    const gap  = (tier.xpRequired ?? 0) - (prev.xpRequired ?? 0);
    const warn = gapWarning(gap, tier.level);
    if (warn) warnings.push({ tier, gap, warn });
  }
  if (warnings.length === 0) {
    return <div className={styles.analyzerOk}>✅ All level gaps look balanced</div>;
  }
  return (
    <div className={styles.analyzerWrap}>
      <div className={styles.analyzerTitle}>⚖️ Gap Analyzer</div>
      <div className={styles.analyzerList}>
        {warnings.map(({ tier, gap, warn }) => (
          <div key={tier.level} className={`${styles.analyzerItem} ${styles['analyzerItem_' + warn.type]}`}>
            <div className={styles.analyzerBadge} style={{ background: tier.color }}>{tier.icon || tier.level}</div>
            <div className={styles.analyzerText}>
              <strong>L{tier.level} {tier.label}</strong>{' — '}
              {warn.type === 'easy'
                ? `gap too small (+${gap.toLocaleString()} XP). Members skip this level easily.`
                : `gap too large (+${gap.toLocaleString()} XP). Members may lose motivation.`}
            </div>
            <div className={`${styles.analyzerTag} ${styles['analyzerTag_' + warn.type]}`}>
              {warn.type === 'easy' ? '⚡ Too easy' : '🧱 Too hard'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── XP Balance Donut Chart (Phase 3) ──────────────────────────────── */
const CHART_COLORS = {
  posts:         '#6366f1',
  comments:      '#10b981',
  enrollments:   '#f59e0b',
  likesPosts:    '#f43f5e',
  likesComments: '#8b5cf6',
};
const CHART_LABELS = {
  posts:         'Posts',
  comments:      'Comments',
  enrollments:   'Enrollments',
  likesPosts:    'Post likes',
  likesComments: 'Comment likes',
};

function XPBalanceChart({ postXp, commXp, enrollXp, lPostXp, lCommXp, rates, setRates, showRates, setShowRates, dailyXp }) {
  const segments = useMemo(() => {
    const raw = {
      posts:         rates.posts         * postXp,
      comments:      rates.comments      * commXp,
      enrollments:   rates.enrollments   * enrollXp,
      likesPosts:    rates.likesPosts    * lPostXp,
      likesComments: rates.likesComments * lCommXp,
    };
    const total = Object.values(raw).reduce((s, v) => s + v, 0) || 1;
    return Object.entries(raw).map(([key, val]) => ({
      key,
      value: val,
      pct:   Math.round((val / total) * 100),
      color: CHART_COLORS[key],
      label: CHART_LABELS[key],
      xpPerUnit: key === 'posts' ? postXp : key === 'comments' ? commXp : key === 'enrollments' ? enrollXp : key === 'likesPosts' ? lPostXp : lCommXp,
    }));
  }, [rates, postXp, commXp, enrollXp, lPostXp, lCommXp]);

  return (
    <div className={styles.chartSection}>
      <div className={styles.chartHeader}>
        <div>
          <div className={styles.chartTitle}>📊 XP Economy Balance</div>
          <div className={styles.chartSub}>
            Daily XP at current rates: <strong>{Math.round(dailyXp).toLocaleString()}</strong> XP/day
          </div>
        </div>
        <button className={styles.ratesToggle} onClick={() => setShowRates(v => !v)}>
          {showRates ? 'Hide assumptions ▲' : 'Edit assumptions ▼'}
        </button>
      </div>

      <div className={styles.chartBody}>
        {/* Donut */}
        <DonutChart segments={segments} size={140} />

        {/* Legend */}
        <div className={styles.chartLegend}>
          {segments.map(seg => (
            <div key={seg.key} className={styles.legendRow}>
              <div className={styles.legendDot} style={{ background: seg.color }} />
              <div className={styles.legendLabel}>{seg.label}</div>
              <div className={styles.legendBar}>
                <div className={styles.legendBarFill} style={{ width: `${seg.pct}%`, background: seg.color }} />
              </div>
              <div className={styles.legendPct}>{seg.pct}%</div>
              <div className={styles.legendXp}>+{seg.xpPerUnit} XP</div>
            </div>
          ))}
        </div>
      </div>

      {/* Activity assumptions editor */}
      {showRates && (
        <div className={styles.ratesEditor}>
          <div className={styles.ratesTitle}>Daily activity assumptions (active member)</div>
          <div className={styles.ratesGrid}>
            {[
              { key: 'posts',         label: 'Posts / day',            step: 0.5 },
              { key: 'comments',      label: 'Comments / day',         step: 1   },
              { key: 'enrollments',   label: 'Enrollments / day',      step: 0.1 },
              { key: 'likesPosts',    label: 'Post likes rcvd / day',  step: 1   },
              { key: 'likesComments', label: 'Cmt likes rcvd / day',   step: 1   },
            ].map(({ key, label, step }) => (
              <div key={key} className={styles.rateItem}>
                <label className={styles.rateLabel}>{label}</label>
                <input
                  type="number"
                  className={styles.rateInput}
                  value={rates[key]}
                  min={0}
                  step={step}
                  onChange={e => setRates(r => ({ ...r, [key]: parseFloat(e.target.value) || 0 }))}
                />
              </div>
            ))}
            <button
              className={styles.rateReset}
              onClick={() => setRates(DEFAULT_RATES)}
            >↺ Reset defaults</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── SVG Donut Chart ────────────────────────────────────────────────── */
function DonutChart({ segments, size = 140 }) {
  const r = size / 2 - 14;
  const cx = size / 2, cy = size / 2;
  const C  = 2 * Math.PI * r;
  const total = segments.reduce((s, seg) => s + seg.value, 0) || 1;

  let accumulated = 0;

  return (
    <svg
      width={size} height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}
    >
      {segments.map(seg => {
        const dash    = (seg.value / total) * C;
        const gap     = C - dash;
        const offset  = -accumulated;
        accumulated  += dash;
        return (
          <circle
            key={seg.key}
            cx={cx} cy={cy} r={r}
            fill="none"
            stroke={seg.color}
            strokeWidth={22}
            strokeDasharray={`${dash} ${gap}`}
            strokeDashoffset={offset}
            strokeLinecap="butt"
          />
        );
      })}
      {/* Center hole */}
      <circle cx={cx} cy={cy} r={r - 14} fill="var(--surface)" />
    </svg>
  );
}
